'use client'

import React, { useCallback, useEffect, useState } from 'react'

interface ScrollToBottomButtonProps {
	containerRef: React.RefObject<HTMLElement | null>
	className?: string
	threshold?: number
}

const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({
	containerRef,
	className = '',
	threshold = 4,
}) => {
	const [isVisible, setIsVisible] = useState(false)

	const updateVisibility = useCallback(() => {
		const container = containerRef.current
		if (!container) {
			setIsVisible(false)
			return
		}

		const isScrollable = container.scrollHeight - container.clientHeight > threshold
		const canScrollDown = container.scrollHeight - container.scrollTop - container.clientHeight > threshold
		setIsVisible(isScrollable && canScrollDown)
	}, [containerRef, threshold])

	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		let frameId = 0
		const scheduleUpdate = () => {
			cancelAnimationFrame(frameId)
			frameId = requestAnimationFrame(updateVisibility)
		}

		const resizeObserver = new ResizeObserver(scheduleUpdate)
		resizeObserver.observe(container)
		Array.from(container.children).forEach((child) => resizeObserver.observe(child))

		const mutationObserver = new MutationObserver(scheduleUpdate)
		mutationObserver.observe(container, {
			attributes: true,
			childList: true,
			subtree: true,
		})

		container.addEventListener('scroll', scheduleUpdate, { passive: true })
		window.addEventListener('resize', scheduleUpdate)
		scheduleUpdate()

		const timeoutId = window.setTimeout(scheduleUpdate, 100)

		return () => {
			cancelAnimationFrame(frameId)
			window.clearTimeout(timeoutId)
			resizeObserver.disconnect()
			mutationObserver.disconnect()
			container.removeEventListener('scroll', scheduleUpdate)
			window.removeEventListener('resize', scheduleUpdate)
		}
	}, [containerRef, updateVisibility])

	const handleClick = () => {
		const container = containerRef.current
		if (!container) return

		container.scrollTo({
			top: container.scrollHeight,
			behavior: 'smooth',
		})
	}

	if (!isVisible) return null

	return (
		<>
			<style>
				{`
					@keyframes scroll-to-bottom-button-bounce {
						0%, 100% { transform: translate(-50%, 0); }
						50% { transform: translate(-50%, 5px); }
					}
				`}
			</style>
			<button
				type="button"
				aria-label="Scroll to bottom"
				title="Scroll to bottom"
				onClick={handleClick}
				className={`absolute bottom-md left-1/2 z-[5] w-[44px] h-[44px] rounded-full border border-primary-600 bg-primary-500 text-white shadow-md flex items-center justify-center font-icon-solid text-[18px] hover:border-primary-700 hover:bg-primary-600 focus:outline-none focus:shadow-focus active:shadow-focus transition-colors ${className}`}
				style={{ animation: 'scroll-to-bottom-button-bounce 1.2s ease-in-out infinite' }}
			>
				
			</button>
		</>
	)
}

export default ScrollToBottomButton
